/**
 * The person, company and additional-owner records Stripe keeps on a
 * merchant, and the formatting they need.
 *
 * All three columns are JSON and arrive either parsed or as a string
 * depending on the read path, so every parser here accepts both.
 */

export interface AddressFields {
  addressLine1?: string | null;
  addressLine2?: string | null;
  addressCity?: string | null;
  addressState?: string | null;
  addressPostalCode?: string | null;
  addressCountry?: string | null;
}

export interface PersonFields extends AddressFields {
  firstName: string;
  lastName: string;
  email?: string | null;
  phone?: string | null;
  dobDay?: number | null;
  dobMonth?: number | null;
  dobYear?: number | null;
  relationshipTitle?: string | null;
  ssnLast4?: string | null;
  isOwner?: boolean | null;
  isDirector?: boolean | null;
  isExecutive?: boolean | null;
  ownershipPercentage?: number | null;
}

export interface AdditionalPerson {
  localId?: string | null;
  stripePersonId?: string | null;
  firstName: string;
  lastName: string;
  email?: string | null;
  dob?: {
    dobDay?: number | null;
    dobMonth?: number | null;
    dobYear?: number | null;
  } | null;
  address?: AddressFields | null;
  isOwner: boolean;
  isDirector: boolean;
  isExecutive?: boolean | null;
  ownershipPercentage?: number | null;
}

export interface CompanyFields extends AddressFields {
  phone?: string | null;
  taxId?: string | null;
  registrationNumber?: string | null;
}

function parseJsonColumn<T>(value: unknown): T | null {
  if (!value) return null;
  try {
    if (typeof value === "string") return JSON.parse(value) as T;
    if (typeof value === "object") return value as T;
  } catch {
    // invalid JSON
  }
  return null;
}

/**
 * Generic because views read different slices of the same record — the
 * sidebar wants a name and contact, the overlay also wants DOB and address —
 * while the parsing itself must not diverge between them.
 */
export function parsePerson<T = PersonFields>(value: unknown): T | null {
  return parseJsonColumn<T>(value);
}

export function parseCompany(value: unknown): CompanyFields | null {
  return parseJsonColumn<CompanyFields>(value);
}

export function parseAdditionalPersons(value: unknown): AdditionalPerson[] {
  if (!value) return [];
  try {
    if (typeof value === "string") {
      const parsed = JSON.parse(value) as unknown;
      return Array.isArray(parsed) ? (parsed as AdditionalPerson[]) : [];
    }
    if (Array.isArray(value)) return value as AdditionalPerson[];
  } catch {
    // invalid JSON
  }
  return [];
}

/**
 * Structurally typed rather than tied to `PersonFields`: additional owners
 * and directors carry the same role flags on a different record.
 */
export function buildRoles(person: {
  isOwner?: boolean | null;
  isDirector?: boolean | null;
  isExecutive?: boolean | null;
  ownershipPercentage?: number | null;
}): string[] {
  const roles: string[] = [];
  if (person.isOwner) {
    const pct =
      person.ownershipPercentage != null
        ? ` (${person.ownershipPercentage}%)`
        : "";
    roles.push(`Owner${pct}`);
  }
  if (person.isDirector) roles.push("Director");
  if (person.isExecutive) roles.push("Executive");
  return roles;
}

/** ISO-shaped, so a date of birth read across locales is never ambiguous. */
export function formatDob(
  day?: number | null,
  month?: number | null,
  year?: number | null,
): string | null {
  if (!day || !month || !year) return null;
  return `${String(year)}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function formatAddress(fields: AddressFields): string | null {
  const parts = [
    fields.addressLine1,
    fields.addressLine2,
    fields.addressCity,
    fields.addressState,
    fields.addressPostalCode,
    fields.addressCountry,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : null;
}
